import { MatchingProfilePreviewCard } from "@/components/mentor-profile/matching-profile-preview-card";
import { Textarea } from "@/components/ui/textarea";
import { BIO_EXAMPLES, BIO_MAX_LENGTH } from "@/lib/mentor-profile/constants";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { WizardStepLayout } from "./wizard-step-layout";
import type { WizardStepProps } from "./types";

export function BioStep({
  draft,
  saveState,
  setField,
  embedded,
  reviewerNotes,
  onContinue,
  onBack,
}: WizardStepProps) {
  const [exampleIndex, setExampleIndex] = useState(0);

  const bio = draft.bio ?? "";
  const isOverLimit = bio.length > BIO_MAX_LENGTH;
  const example = BIO_EXAMPLES[exampleIndex];

  return (
    <WizardStepLayout
      reviewerNotes={reviewerNotes}
      embedded={embedded}
      stepIndex={4}
      icon={
        <img
          src="/assets/mentor-onboarding/step-bio-icon.png"
          alt=""
          className="size-10 object-contain"
        />
      }
      heading="Tell teens a bit about you"
      subheading="Keep it real and easygoing — like how you'd introduce yourself to someone new."
      saveState={saveState}
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={bio.trim().length === 0 || isOverLimit}
      preview={
        <MatchingProfilePreviewCard
          draft={draft}
          focus={{ region: "bio", zoom: 1.05 }}
        />
      }
    >
      <label className="font-body text-sm font-medium text-[#1f1e1d]">
        Short bio
      </label>
      <Textarea
        placeholder="What you're about, what you like talking about, why you wanted to mentor…"
        rows={3}
        maxLength={BIO_MAX_LENGTH}
        value={bio}
        onChange={(e) => setField("bio", e.target.value)}
        className="min-h-[96px] rounded-[16.8px] border-[#e2dccf] font-body text-base text-[#1f1e1d] placeholder:text-[rgba(121,119,116,0.7)]"
      />
      <div className="flex items-center justify-between gap-3">
        <span className="font-body text-sm text-[#797774]">
          Around two sentences is the sweet spot, with a casual and upbeat tone.
        </span>
        <span
          className={cn(
            "font-mono text-xs tabular-nums text-[#797774]",
            isOverLimit && "text-states-error",
          )}
        >
          {bio.length}/{BIO_MAX_LENGTH}
        </span>
      </div>

      <div className="mt-2 rounded-[16.8px] bg-[#f3eee6] px-3.5 py-3">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs font-medium text-[rgba(31,30,29,0.7)]">
            <img
              src="/assets/mentor-onboarding/icon-examples-sparkle.svg"
              alt=""
              className="size-3.5"
            />
            Examples from other mentors
          </span>
          <button
            type="button"
            onClick={() => setExampleIndex((i) => (i + 1) % BIO_EXAMPLES.length)}
            className="flex items-center gap-1.5 rounded-full px-2 py-1.5 text-xs font-medium text-[rgba(31,30,29,0.7)] transition-colors hover:bg-[#e9e2d5]"
          >
            <img
              src="/assets/mentor-onboarding/icon-refresh.svg"
              alt=""
              className="size-3.5"
            />
            Show another
          </button>
        </div>
        <p className="mt-1.5 text-sm leading-[1.4] text-[rgba(31,30,29,0.8)]">
          {example.text}
        </p>
      </div>
    </WizardStepLayout>
  );
}
